import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDirection } from '../hooks/useDirection';

type LanguageCode = 'en' | 'de' | 'fr' | 'he' | 'ru' | 'ar';

interface Language {
  code: LanguageCode;
  displayCode: string;
  nativeName: string;
}

const languages: Language[] = [
  { code: 'en', displayCode: 'EN', nativeName: 'English' },
  { code: 'de', displayCode: 'DE', nativeName: 'Deutsch' },
  { code: 'fr', displayCode: 'FR', nativeName: 'Français' },
  { code: 'ru', displayCode: 'RU', nativeName: 'Русский' },
  { code: 'ar', displayCode: 'AR', nativeName: 'العربية' },
  { code: 'he', displayCode: 'HE', nativeName: 'עברית' },
];

export function LanguagePicker() {
  const { i18n } = useTranslation();
  const { language, setLanguage } = useDirection();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = languages.find((l) => l.code === language) || languages[0];

  const handleLanguageChange = (newLang: LanguageCode) => {
    i18n.changeLanguage(newLang);
    setLanguage(newLang);
    setIsOpen(false);
  };

  return (
    <div className="dropdown" ref={dropdownRef}>
      <button
        className="btn btn-link nav-link dropdown-toggle"
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {currentLang.displayCode}
      </button>
      <ul
        className={`dropdown-menu dropdown-menu-end ${isOpen ? 'show' : ''}`}
        aria-labelledby="language-picker"
      >
        {languages.map((lang) => (
          <li key={lang.code}>
            <button
              className={`dropdown-item ${lang.code === currentLang.code ? 'active' : ''}`}
              onClick={() => handleLanguageChange(lang.code)}
              lang={lang.code}
            >
              {lang.displayCode} | {lang.nativeName}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
